import { Clock, Plus, Star } from "lucide-react";
import { useId, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { BrebReceivingKey } from "@/shared/services/payments/brebReceivingKey.service";
import { colocaToast } from "@/shared/utils/toast";

import { BREB_KEY_SOURCE_ICON, BREB_KEY_SOURCE_NAME, BREB_KEY_TONE } from "./brebKeySources";

export interface BrebKeyListProps {
  keys: BrebReceivingKey[];
  selectedId: string;
  onSelect: (keyId: string) => void;
  /** Absent when no other option can be added, or the user may not create keys. */
  onAdd?: () => void;
}

export function BrebKeyList({ keys, selectedId, onSelect, onAdd }: BrebKeyListProps) {
  const { t } = useTranslations("payins");
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 id={titleId} className="text-sm font-semibold text-foreground">
            {t("breb_key_list_title")}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("breb_key_list_hint")}</p>
        </div>
        {onAdd && (
          <ColocaButton size="sm" variant="ghost" icon={Plus} onClick={onAdd}>
            {t("breb_key_list_add")}
          </ColocaButton>
        )}
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {keys.map((key) => {
          const Icon = BREB_KEY_SOURCE_ICON[key.source];
          const selected = key.id === selectedId;
          const pending = key.status === "PENDING";
          return (
            <li key={key.id}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onSelect(key.id)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transition-none",
                  selected
                    ? "border-primary bg-primary/5"
                    : "border-border bg-card hover:border-primary/40 hover:bg-primary/5",
                )}
              >
                <span
                  className={cn(
                    "flex size-9 shrink-0 items-center justify-center rounded-full text-white",
                    pending ? BREB_KEY_TONE.pending : BREB_KEY_TONE.live,
                  )}
                >
                  <Icon className="size-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-sm font-semibold text-foreground">
                    {key.key_value}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">
                      {t(BREB_KEY_SOURCE_NAME[key.source])}
                    </span>
                    {key.is_primary && (
                      <ColocaBadge variant="success" icon={Star}>
                        {t("breb_key_primary_badge")}
                      </ColocaBadge>
                    )}
                    {pending && (
                      <ColocaBadge variant="secondary" icon={Clock}>
                        {t("breb_key_pending_badge")}
                      </ColocaBadge>
                    )}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export interface BrebKeySetPrimaryActionProps {
  keyValue: string;
  onSetPrimary: () => Promise<unknown>;
}

export function BrebKeySetPrimaryAction({ keyValue, onSetPrimary }: BrebKeySetPrimaryActionProps) {
  const { t } = useTranslations("payins");
  const [saving, setSaving] = useState(false);

  const setPrimary = async () => {
    setSaving(true);
    try {
      await onSetPrimary();
      colocaToast.success(t("breb_key_set_primary_done", { key: keyValue }));
    } catch (error) {
      colocaToast.error(HttpApiService.getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ColocaButton size="sm" variant="default" icon={Star} loading={saving} onClick={() => void setPrimary()}>
      {t("breb_key_set_primary")}
    </ColocaButton>
  );
}
